/**
 * audioService.js
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE Audio element shared across ALL AudioCard instances.
 *
 * Instead of each card owning its own `new Audio()`, every card calls this
 * service to play/pause. The service tracks which card is active and notifies
 * cards via a simple callback registry.
 *
 * Cards never touch the Audio element directly — they only call:
 *   audioService.play(id, url, onProgress, onEnd)
 *   audioService.pause(id)
 *   audioService.destroy(id)     ← call in beforeUnmount
 */

// ─── Single shared Audio element ─────────────────────────────────────────────
const _audio = new Audio()
_audio.preload = 'metadata'

// ─── State ────────────────────────────────────────────────────────────────────
let _activeId      = null   // id of the currently playing card
let _onProgress    = null   // progress callback of the active card
let _onEnd         = null   // end callback of the active card
let _stopCallbacks = {}     // id → fn called when that card is forcibly stopped

// ─── Internal listeners (attached once, never removed) ────────────────────────
_audio.addEventListener('timeupdate', () => {
  if (!_onProgress || !_audio.duration) return
  _onProgress((_audio.currentTime / _audio.duration) * 100)
})

_audio.addEventListener('ended', () => {
  const cb = _onEnd
  _cleanup()
  if (cb) cb()
})

function _cleanup() {
  _activeId   = null
  _onProgress = null
  _onEnd      = null
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Start playing audio for a card.
 * If another card is currently playing it is stopped first.
 *
 * @param {string}   id          Unique card id (e.g. audio filename or _id)
 * @param {string}   url         Full audio URL
 * @param {Function} onProgress  Called with (percent 0-100) on each timeupdate
 * @param {Function} onEnd       Called when audio finishes naturally
 */
function play(id, url, onProgress, onEnd) {
  // Stop the previously active card (notify it so it can update its UI)
  if (_activeId && _activeId !== id) {
    const stopCb = _stopCallbacks[_activeId]
    if (stopCb) stopCb()
  }

  _activeId   = id
  _onProgress = onProgress
  _onEnd      = onEnd

  // Only change src if we're switching to a different track
  if (_audio.src !== url) {
    _audio.src = url
    _audio.load()
  }

  _audio.play().catch(err => console.warn('AudioService play failed:', err))
}

/**
 * Pause the audio. Only effective if the calling card is the active one.
 * @param {string} id
 */
function pause(id) {
  if (_activeId !== id) return
  _audio.pause()
}

/**
 * Resume after pause. Only effective if the calling card is the active one.
 * @param {string} id
 */
function resume(id) {
  if (_activeId !== id) return
  _audio.play().catch(err => console.warn('AudioService resume failed:', err))
}

/**
 * Seek to a ratio position (0–1). Only effective for the active card.
 * @param {string} id
 * @param {number} ratio  0.0 – 1.0
 */
function seek(id, ratio) {
  if (_activeId !== id || !_audio.duration) return
  _audio.currentTime = ratio * _audio.duration
}

/**
 * Set playback speed. Only effective for the active card.
 * @param {string} id
 * @param {number} rate  0.5 – 2.0
 */
function setRate(id, rate) {
  if (_activeId !== id) return
  _audio.playbackRate = rate
}

/**
 * Register a callback that fires when this card is stopped by another card
 * starting playback. Call in mounted().
 * @param {string}   id
 * @param {Function} cb
 */
function onForcedStop(id, cb) {
  _stopCallbacks[id] = cb
}

/**
 * Unregister everything for a card. Call in beforeUnmount().
 * @param {string} id
 */
function destroy(id) {
  delete _stopCallbacks[id]
  if (_activeId === id) {
    _audio.pause()
    _cleanup()
  }
}

/**
 * Whether the given card is the currently active (playing) one.
 * @param {string} id
 * @returns {boolean}
 */
function isActive(id) {
  return _activeId === id
}

export default { play, pause, resume, seek, setRate, onForcedStop, destroy, isActive }